import api from './api';
import { ImportMetadata, PdfImportDiagnostics, Song } from '../types/music';

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  suggestedFix?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  suggestions: string[];
  confidence: number; // 0-1
  summary: {
    totalErrors: number;
    totalWarnings: number;
    sectionsCount: number;
    chordsCount: number;
    linesCount: number;
  };
}

export interface ImportResponse {
  success: boolean;
  message: string;
  draft?: boolean;
  recovered?: boolean;
  song?: Song & { importMetadata?: ImportMetadata };
  validation?: ValidationResult;
  metadata?: {
    filename: string;
    size: number;
    mimeType: string;
    processingTime: number;
    processedAt: string;
    confidence: number;
    provider?: string;
    models?: {
      content?: string;
      placement?: string;
    };
    pdfStrategy?: string;
    pdfImageFallbackUsed?: boolean;
    pdfTextPathUsed?: boolean;
    aiInputMimeType?: string;
    pdfDiagnostics?: PdfImportDiagnostics | null;
  };
  error?: string;
  code?: string;
  retryable?: boolean;
  retryDelay?: number;
  parsedData?: Song;
}

export interface ImportHealthResponse {
  status: 'healthy' | 'unhealthy';
  services?: {
    geminiApi: {
      status: string;
      endpoint?: string;
    };
    validation: {
      status: string;
      patterns?: number;
    };
    database: {
      status: string;
    };
  };
  configuration?: {
    maxFileSize: string;
    timeout: string;
    supportedFormats: string[];
  };
  error?: string;
  timestamp?: string;
}

export interface ImportProgressCallback {
  (loaded: number, total: number): void;
}

export interface ImportOptions {
  onProgress?: ImportProgressCallback;
  timeout?: number;
  signal?: AbortSignal;
}

class ImportService {
  private getDefaultTimeout(file: File): number {
    if (file.type === 'application/pdf') {
      return 10 * 60 * 1000;
    }

    return 2 * 60 * 1000;
  }

  // Import a song from file
  async importSong(
    file: File,
    options: ImportOptions = {},
  ): Promise<ImportResponse> {
    try {
      // Validate file before upload
      this.validateFile(file);

      const formData = new FormData();
      formData.append('file', file);

      // Create request config with progress tracking
      const config = {
        timeout: options.timeout || this.getDefaultTimeout(file),
        signal: options.signal,
        onUploadProgress: (progressEvent: any) => {
          if (options.onProgress && progressEvent.total) {
            const loaded = progressEvent.loaded || 0;
            const total = progressEvent.total || 0;
            options.onProgress(loaded, total);
          }
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await api.post<ImportResponse>(
        '/api/songs/import',
        formData,
        config,
      );

      return this.normalizeImportResponse(response.data);
    } catch (error: any) {
      console.error('Failed to import song:', error);

      // Handle different error types
      if (error.response?.data) {
        return this.normalizeImportResponse(
          error.response.data as ImportResponse,
        );
      }

      // Network or timeout errors
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        const isPdf = file.type === 'application/pdf';
        return {
          success: false,
          message: isPdf
            ? 'PDF import timed out. Image-based PDFs can take several minutes; please try again.'
            : 'Import timeout - please try again',
          error: 'TIMEOUT',
          code: 'TIMEOUT',
          retryable: true,
          retryDelay: 5000,
        };
      }

      if (error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: 'Network error - please check your connection',
          error: 'NETWORK_ERROR',
          code: 'NETWORK_ERROR',
          retryable: true,
          retryDelay: 5000,
        };
      }

      // Generic error
      return {
        success: false,
        message: 'Import failed - please try again',
        error: error.message || 'Unknown error',
        code: 'UNKNOWN_ERROR',
        retryable: false,
      };
    }
  }

  // Check import service health
  async checkHealth(): Promise<ImportHealthResponse> {
    try {
      const response = await api.get<ImportHealthResponse>(
        '/api/songs/import/health',
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to check import health:', error);

      return {
        status: 'unhealthy',
        error: error.response?.data?.error || 'Service unavailable',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Validate file before upload
  private validateFile(file: File): void {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const supportedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];

    if (!file) {
      throw new Error('No file provided');
    }

    if (file.size > maxSize) {
      throw new Error(
        `File too large. Maximum size is ${maxSize / (1024 * 1024)}MB`,
      );
    }

    if (!supportedTypes.includes(file.type)) {
      throw new Error(
        `Unsupported file type: ${file.type}. Supported types: PDF, JPEG, PNG, WebP`,
      );
    }
  }

  // Get supported file types for validation
  getSupportedFileTypes(): string[] {
    return ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
  }

  // Get maximum file size
  getMaxFileSize(): number {
    return 50 * 1024 * 1024; // 50MB
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Check if file type is supported
  isFileTypeSupported(file: File): boolean {
    const supportedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];
    return supportedTypes.includes(file.type);
  }

  // Create retry delay with exponential backoff
  calculateRetryDelay(attempt: number, baseDelay: number = 1000): number {
    return Math.min(baseDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
  }

  // Extract filename from path or file object
  getFileName(file: File | string): string {
    if (typeof file === 'string') {
      return file.split('/').pop() || 'unknown';
    }
    return file.name;
  }

  private normalizeImportResponse(response: ImportResponse): ImportResponse {
    if (!response.song) {
      return response;
    }

    return {
      ...response,
      song: this.normalizeImportedSong(response.song, response.draft === true),
    };
  }

  private normalizeImportedSong(song: Song, draft: boolean): Song {
    const localIdPrefix = `local_import_${Date.now()}_`;
    const normalizedMetadata = song.importMetadata
      ? {
          ...song.importMetadata,
          importedAt: song.importMetadata.importedAt
            ? new Date(song.importMetadata.importedAt)
            : undefined,
        }
      : undefined;

    return {
      ...song,
      _id: draft ? `${localIdPrefix}song` : String(song._id),
      isLocalDraft: draft || song.isLocalDraft,
      createdAt: new Date(song.createdAt),
      modifiedAt: new Date(song.modifiedAt),
      lastSyncedAt: song.lastSyncedAt ? new Date(song.lastSyncedAt) : undefined,
      importMetadata: normalizedMetadata,
      sections: (song.sections || []).map((section, sectionIndex) => ({
        ...section,
        id: section.id || `${localIdPrefix}section_${sectionIndex}`,
        lines: (section.lines || []).map((line, lineIndex) => ({
          ...line,
          id: line.id || `${localIdPrefix}line_${sectionIndex}_${lineIndex}`,
          chords: (line.chords || []).map((chord, chordIndex) => ({
            ...chord,
            id:
              chord.id ||
              `${localIdPrefix}chord_${sectionIndex}_${lineIndex}_${chordIndex}`,
          })),
        })),
      })),
    };
  }
}

const importService = new ImportService();
export default importService;
